import { z } from "zod";
import { STORAGE_CATEGORIES } from "../domain/media";
import { PLAN_IDS } from "../domain/plans";
import { ORG_ROLES } from "../domain/roles";

/**
 * Operations & platform administration (docs/13-betrieb.md): status snapshot,
 * alerts, backups, admin area and the editable legal pages.
 */

export const HEALTH_LEVELS = ["ok", "warning", "critical", "unknown"] as const;
export type HealthLevel = (typeof HEALTH_LEVELS)[number];
export const healthLevelSchema = z.enum(HEALTH_LEVELS);

export const ALERT_SEVERITIES = ["warning", "critical"] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

export const SERVICE_IDS = ["database", "valkey", "api", "worker", "backup", "payments"] as const;
export type ServiceId = (typeof SERVICE_IDS)[number];

export const serviceStatusSchema = z.object({
  id: z.enum(SERVICE_IDS),
  label: z.string(),
  status: healthLevelSchema,
  detail: z.string(),
  latencyMs: z.number().nullable(),
});
export type ServiceStatus = z.infer<typeof serviceStatusSchema>;

export const requestStatsSchema = z.object({
  windowSeconds: z.number(),
  total: z.number(),
  errors5xx: z.number(),
  p50Ms: z.number().nullable(),
  p95Ms: z.number().nullable(),
});
export type RequestStats = z.infer<typeof requestStatsSchema>;

/** Published by the api and worker processes every 30 seconds (heartbeat). */
export const processInfoSchema = z.object({
  role: z.enum(["api", "worker"]),
  version: z.string(),
  pid: z.number(),
  startedAt: z.coerce.date(),
  lastSeen: z.coerce.date(),
  rssBytes: z.number(),
  heapUsedBytes: z.number(),
  /** Container memory (cgroup), null outside a container. */
  memoryUsedBytes: z.number().nullable(),
  memoryLimitBytes: z.number().nullable(),
  eventLoopLagMs: z.number(),
  requests: requestStatsSchema.nullable(),
});
export type ProcessInfo = z.infer<typeof processInfoSchema>;

export const queueStatsSchema = z.object({
  name: z.string(),
  label: z.string(),
  waiting: z.number(),
  active: z.number(),
  delayed: z.number(),
  failed: z.number(),
  failedLastHour: z.number(),
  oldestWaitingSeconds: z.number().nullable(),
});
export type QueueStats = z.infer<typeof queueStatsSchema>;

export const hostStatsSchema = z.object({
  diskTotalBytes: z.number(),
  diskUsedBytes: z.number(),
  diskPercent: z.number(),
  memTotalBytes: z.number(),
  memAvailableBytes: z.number(),
  load1: z.number(),
  load5: z.number(),
  load15: z.number(),
  cpus: z.number(),
});
export type HostStats = z.infer<typeof hostStatsSchema>;

export const appStatsSchema = z.object({
  users: z.number(),
  usersNew7d: z.number(),
  activeUsers7d: z.number(),
  orgs: z.number(),
  documents: z.number(),
  assets: z.number(),
  storageBytes: z.number(),
});
export type AppStats = z.infer<typeof appStatsSchema>;

export const BACKUP_KINDS = ["daily", "pre-deploy", "manual", "offsite", "verify"] as const;
export type BackupKind = (typeof BACKUP_KINDS)[number];

export const BACKUP_KIND_LABELS: Record<BackupKind, string> = {
  daily: "Täglich (Datenbank)",
  "pre-deploy": "Vor Deploy",
  manual: "Manuell",
  offsite: "Off-Site (Datenbank + Medien)",
  verify: "Wiederherstellungstest",
};

export const backupRunSchema = z.object({
  id: z.string(),
  kind: z.enum(BACKUP_KINDS),
  status: z.enum(["ok", "failed"]),
  startedAt: z.date(),
  finishedAt: z.date(),
  sizeBytes: z.number().nullable(),
  file: z.string().nullable(),
  message: z.string().nullable(),
});
export type BackupRun = z.infer<typeof backupRunSchema>;

export const alertSchema = z.object({
  id: z.string(),
  key: z.string(),
  severity: z.enum(ALERT_SEVERITIES),
  title: z.string(),
  message: z.string(),
  status: z.enum(["open", "resolved"]),
  openedAt: z.date(),
  resolvedAt: z.date().nullable(),
  lastNotifiedAt: z.date().nullable(),
  notifyCount: z.number(),
});
export type Alert = z.infer<typeof alertSchema>;

/** Buckets of the rate limits (apps/backend/src/lib/rate-limit.ts) plus the queue cap. */
export const RATE_LIMIT_SCOPE_LABELS: Record<string, string> = {
  api: "API (pro Person)",
  live: "Live-Zusammenarbeit (pro Person)",
  public: "Ohne Anmeldung (pro IP)",
  shareFiles: "Dateien auf Share-Links (pro IP)",
  forms: "Kündigung & Gastkommentare (pro IP)",
  mcp: "MCP (pro Verbindung)",
  exports: "Exporte (pro Organisation)",
  cutouts: "Freistellen (pro Organisation)",
  transcriptions: "Spracherkennung (pro Organisation)",
  uploads: "Uploads (pro Organisation)",
  documents: "Neue Dateien (pro Organisation)",
  mcpWrites: "MCP-Änderungen (pro Organisation)",
  mcpRenders: "MCP-Seitenbilder (pro Organisation)",
  auth: "Anmeldedienst (pro IP)",
  authStrict: "Anmelden, Registrieren, 2FA, Passwort (pro IP)",
  authMail: "E-Mails anfordern (pro IP)",
  queue: "Warteschlange voll (pro Organisation)",
};

export const rateLimitStatsSchema = z.object({
  enabled: z.boolean(),
  /** Refused requests of the last hour / five minutes, all API processes together. */
  lastHour: z.number(),
  last5m: z.number(),
  byScope: z.array(z.object({ scope: z.string(), lastHour: z.number(), last5m: z.number() })),
});
export type RateLimitStats = z.infer<typeof rateLimitStatsSchema>;

export const opsStatusSchema = z.object({
  generatedAt: z.date(),
  env: z.string(),
  version: z.string(),
  overall: healthLevelSchema,
  services: z.array(serviceStatusSchema),
  processes: z.array(processInfoSchema),
  queues: z.array(queueStatsSchema),
  host: hostStatsSchema,
  app: appStatsSchema,
  backups: z.object({
    lastDaily: backupRunSchema.nullable(),
    lastPreDeploy: backupRunSchema.nullable(),
    lastOffsite: backupRunSchema.nullable(),
    lastVerify: backupRunSchema.nullable(),
    /** A backup is expected on this environment (server stack). */
    expected: z.boolean(),
  }),
  alerts: z.array(alertSchema),
  notifications: z.object({
    smtp: z.boolean(),
    emailRecipients: z.number(),
    webhook: z.boolean(),
    heartbeat: z.boolean(),
    alertsEnabled: z.boolean(),
  }),
  /** First metric sample: how long the monitoring has been collecting data. */
  monitoringSince: z.date().nullable(),
  /** Stripe (docs/16-geschaeftsmodell.md); null = payment not set up here. */
  payments: z
    .object({
      lastWebhookAt: z.date().nullable(),
      /** Webhooks of the last 24 hours we could not process. */
      problems24h: z.number(),
      lastProblem: z.string().nullable(),
      /** Cancellations ("Verträge hier kündigen") an admin still has to handle. */
      openCancellations: z.number(),
    })
    .nullable(),
  rateLimits: rateLimitStatsSchema,
});
export type OpsStatus = z.infer<typeof opsStatusSchema>;

export const metricSampleSchema = z.object({
  ts: z.date(),
  diskPercent: z.number(),
  memAvailableBytes: z.number(),
  load1: z.number(),
  apiRssBytes: z.number().nullable(),
  workerMemoryBytes: z.number().nullable(),
  workerMemoryLimitBytes: z.number().nullable(),
  queueWaiting: z.number(),
  queueActive: z.number(),
  failedLastHour: z.number(),
  requests: z.number(),
  errors5xx: z.number(),
  p95Ms: z.number().nullable(),
});
export type MetricSample = z.infer<typeof metricSampleSchema>;

// --- Admin area ---------------------------------------------------------------

export const adminSessionSchema = z.object({
  isAdmin: z.boolean(),
  twoFactorEnabled: z.boolean(),
  /** 2FA is required for the admin area on this server. */
  requires2fa: z.boolean(),
  /** The 2FA code was entered for this session and is still valid. */
  unlocked: z.boolean(),
  unlockedUntil: z.date().nullable(),
  openAlerts: z.object({ warning: z.number(), critical: z.number() }).nullable(),
});
export type AdminSession = z.infer<typeof adminSessionSchema>;

export const PLATFORM_ROLES = ["user", "admin"] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export const adminUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  createdAt: z.date(),
  lastActiveAt: z.date().nullable(),
  role: z.enum(PLATFORM_ROLES),
  banned: z.boolean(),
  banReason: z.string().nullable(),
  banExpires: z.date().nullable(),
  twoFactorEnabled: z.boolean(),
  orgCount: z.number(),
  storageBytes: z.number(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

export const auditEntrySchema = z.object({
  id: z.string(),
  actorId: z.string().nullable(),
  actorEmail: z.string(),
  action: z.string(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  details: z.record(z.string(), z.unknown()),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.date(),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export const adminUserDetailSchema = adminUserSchema.extend({
  memberships: z.array(
    z.object({
      orgId: z.string(),
      orgName: z.string(),
      role: z.enum(ORG_ROLES).or(z.string()),
      joinedAt: z.date(),
    }),
  ),
  sessions: z.array(
    z.object({
      id: z.string(),
      createdAt: z.date(),
      updatedAt: z.date(),
      expiresAt: z.date(),
      ipAddress: z.string().nullable(),
      userAgent: z.string().nullable(),
    }),
  ),
  loginMethods: z.array(z.string()),
  connectedApps: z.array(
    z.object({ clientId: z.string(), name: z.string().nullable(), createdAt: z.date() }),
  ),
  audit: z.array(auditEntrySchema),
});
export type AdminUserDetail = z.infer<typeof adminUserDetailSchema>;

export const adminOrgSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  createdAt: z.date(),
  memberCount: z.number(),
  projectCount: z.number(),
  documentCount: z.number(),
  assetCount: z.number(),
  storageBytes: z.number(),
  plan: z.enum(PLAN_IDS),
  quotaBytes: z.number(),
  /** No own quota: the plan's storage (or DEFAULT_ORG_QUOTA_GB) applies. */
  quotaIsDefault: z.boolean(),
});
export type AdminOrg = z.infer<typeof adminOrgSchema>;

export const adminOrgDetailSchema = adminOrgSchema.extend({
  members: z.array(
    z.object({
      userId: z.string(),
      name: z.string(),
      email: z.string(),
      role: z.string(),
      joinedAt: z.date(),
    }),
  ),
  storageByCategory: z.array(
    z.object({ category: z.enum(STORAGE_CATEGORIES).or(z.string()), bytes: z.number() }),
  ),
  lastActivityAt: z.date().nullable(),
  /** Billable usage of the current calendar month and brand kits. */
  usage: z.object({ captionSeconds: z.number(), cutouts: z.number(), brandKits: z.number() }),
});
export type AdminOrgDetail = z.infer<typeof adminOrgDetailSchema>;

export const failedJobSchema = z.object({
  queue: z.string(),
  id: z.string(),
  name: z.string(),
  failedReason: z.string(),
  attemptsMade: z.number(),
  createdAt: z.date(),
  finishedAt: z.date().nullable(),
  data: z.string(),
});
export type FailedJob = z.infer<typeof failedJobSchema>;

/** Human-readable names for audit actions. */
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  "admin.unlock": "Admin-Bereich entsperrt (2FA)",
  "cancellation.resolve": "Kündigung als erledigt markiert",
  "admin.unlock_failed": "2FA-Code falsch",
  "user.view": "Konto angesehen",
  "user.ban": "Konto gesperrt",
  "user.unban": "Sperre aufgehoben",
  "user.revoke_sessions": "Sitzungen beendet",
  "user.grant_admin": "Admin-Rechte vergeben",
  "user.revoke_admin": "Admin-Rechte entzogen",
  "org.view": "Organisation angesehen",
  "org.set_quota": "Speicher-Kontingent geändert",
  "org.set_plan": "Tarif geändert",
  "org.add_member": "Mitglied hinzugefügt",
  "org.set_member_role": "Rolle eines Mitglieds geändert",
  "org.remove_member": "Mitglied entfernt",
  "job.retry": "Job erneut gestartet",
  "job.remove": "Job entfernt",
  "alert.test": "Test-Benachrichtigung gesendet",
  "legal.update": "Rechtliche Angaben geändert",
  "cli.grant_admin": "Admin-Rechte vergeben (Server-Befehl)",
  "cli.revoke_admin": "Admin-Rechte entzogen (Server-Befehl)",
  "cli.reset_2fa": "2FA zurückgesetzt (Server-Befehl)",
};

// --- Legal pages ----------------------------------------------------------------

const text = (max: number) => z.string().trim().max(max).default("");

/** Operator data for imprint, privacy policy and terms, edited in the admin area. */
export const legalSettingsSchema = z.object({
  operatorName: text(200),
  legalForm: text(100),
  representative: text(200),
  street: text(200),
  postalCode: text(20),
  city: text(100),
  country: text(100),
  email: text(200),
  phone: text(50),
  vatId: text(50),
  registerCourt: text(100),
  registerNumber: text(100),
  /** § 18 Abs. 2 MStV: name and address, if different from the operator. */
  contentResponsible: text(300),
  /** Data protection officer or contact for privacy requests. */
  privacyContact: text(300),
  supervisoryAuthority: text(300),
  hostingProvider: text(300),
  emailProvider: text(300),
  /** Off-site backup storage provider (restic target), if any. */
  backupProvider: text(300),
  extraImprint: text(5000),
  extraPrivacy: text(20000),
  extraTerms: text(20000),
});
export type LegalSettings = z.infer<typeof legalSettingsSchema>;

/** Fields that must be filled before the pages are complete. */
export const LEGAL_REQUIRED_FIELDS = [
  "operatorName",
  "street",
  "postalCode",
  "city",
  "email",
  "hostingProvider",
] as const satisfies readonly (keyof LegalSettings)[];

export const LEGAL_FIELD_LABELS: Record<keyof LegalSettings, string> = {
  operatorName: "Name bzw. Firma",
  legalForm: "Rechtsform",
  representative: "Vertreten durch",
  street: "Straße und Hausnummer",
  postalCode: "PLZ",
  city: "Ort",
  country: "Land",
  email: "E-Mail",
  phone: "Telefon",
  vatId: "USt-IdNr.",
  registerCourt: "Registergericht",
  registerNumber: "Registernummer",
  contentResponsible: "Verantwortlich nach § 18 Abs. 2 MStV",
  privacyContact: "Datenschutz-Kontakt / Datenschutzbeauftragte:r",
  supervisoryAuthority: "Zuständige Aufsichtsbehörde",
  hostingProvider: "Hosting-Anbieter und Serverstandort",
  emailProvider: "E-Mail-Versand (SMTP-Anbieter)",
  backupProvider: "Speicherort der Off-Site-Backups",
  extraImprint: "Zusätzlicher Text Impressum",
  extraPrivacy: "Zusätzlicher Text Datenschutz",
  extraTerms: "Zusätzlicher Text Nutzungsbedingungen",
};

export const legalPageSchema = z.object({
  settings: legalSettingsSchema,
  updatedAt: z.date().nullable(),
  missing: z.array(z.string()),
  /** Facts about this installation the privacy policy depends on. */
  features: z.object({
    googleLogin: z.boolean(),
    smtp: z.boolean(),
    signupEnabled: z.boolean(),
    trashDays: z.number(),
    exportDays: z.number(),
    /** Paid plans with Stripe are set up on this server. */
    billing: z.boolean(),
  }),
});
export type LegalPage = z.infer<typeof legalPageSchema>;

// --- Landing page statistics (docs/18-landingpage.md) ----------------------------------

export const TRAFFIC_DEVICES = ["desktop", "mobile", "tablet"] as const;
export type TrafficDevice = (typeof TRAFFIC_DEVICES)[number];
export const TRAFFIC_DEVICE_LABELS: Record<TrafficDevice, string> = {
  desktop: "Computer",
  mobile: "Smartphone",
  tablet: "Tablet",
};
/** Source values that are not a host name. */
export const TRAFFIC_SOURCE_LABELS: Record<string, string> = {
  direct: "Direkt oder unbekannt",
  internal: "Auf der Seite selbst",
};

const trafficCountSchema = z.object({ views: z.number(), visitors: z.number() });

/** Page views and unique visitors (per day) of the landing page, without any personal data. */
export const trafficStatsSchema = z.object({
  days: z.number(),
  totals: trafficCountSchema,
  /** One entry per day of the range, oldest first, days without visits included. */
  byDay: z.array(trafficCountSchema.extend({ day: z.string() })),
  byPage: z.array(z.object({ path: z.string(), views: z.number() })),
  byDevice: z.array(trafficCountSchema.extend({ device: z.enum(TRAFFIC_DEVICES) })),
  byBrowser: z.array(trafficCountSchema.extend({ browser: z.string() })),
  /** Where visitors came from (counted once per visitor and day). */
  bySource: z.array(z.object({ source: z.string(), visitors: z.number() })),
});
export type TrafficStats = z.infer<typeof trafficStatsSchema>;
